'use client';

import InlineStyle from '@/components/InlineStyle';
import { Lightbulb, Users, Sparkles, GraduationCap, Heart, Scale, Eye, Target, Handshake, Wallet, ShieldPlus, Building2, PartyPopper, Award, ArrowDown } from 'lucide-react';

/* Content verbatim from the approved Careers page brief. */
import { useTranslation } from "@/hooks/useTranslation";
const VALUES = [{
  id: 'innovative-spirit',
  Icon: Lightbulb,
  title: 'Innovative Spirit',
  desc: 'Innovation is the name of the game and we want you to be a part of this game-changing journey with your new ideas and techniques. We are here to breathe life into those ideas.'
}, {
  id: 'collaborative-culture',
  Icon: Users,
  title: 'Collaborative Culture',
  desc: "Join a community where teamwork and mutual support fuel extraordinary outcomes. At Locobuzz, your voice is heard, and your contributions matter."
}, {
  id: 'impactful-work',
  Icon: Sparkles,
  title: 'Impactful Work',
  desc: 'Make a tangible difference for brands and their customers across the globe. Every project you work on has the potential to enhance customer experiences.'
}, {
  id: 'learning-and-growth',
  Icon: GraduationCap,
  title: 'Learning and Growth',
  desc: "Learning never stops. That's why we are keen to invest in your development because your advancement drives ours. Embrace continuous growth through formats of your choice."
}, {
  id: 'deliver-delight',
  Icon: Heart,
  title: 'Deliver Delight',
  desc: 'Customer delight is our number one priority. We turn ordinary interactions into memorable moments by offering unexpected value, intuitive solutions, and engaging experiences.'
}, {
  id: 'work-life-harmony',
  Icon: Scale,
  title: 'Work-Life Harmony',
  desc: 'We value productivity and well-being in equal measure. So add some flavour to life with flexible working arrangements that cater to different needs and life stages.'
}, {
  id: 'transparency',
  Icon: Eye,
  title: 'Transparency',
  desc: 'We operate with openness and integrity, ensuring that our team and our clients always know what to expect from us. This is our way to ensure that we foster trust and ownership of our actions and their outcomes.'
}, {
  id: 'customer-first',
  Icon: Target,
  title: 'Customer First',
  desc: 'Every decision and innovation at Locobuzz is guided by the needs and experiences of our customers, ensuring that we always deliver for efficacy as well as transformation.'
}, {
  id: 'say-do',
  Icon: Handshake,
  title: 'Say : Do',
  desc: 'Our ethos is simple — we keep our promises. Internal agreements or promises made to clients, we take our word seriously and ensure deliverance on time, every time.'
}];
const BENEFITS = [{
  id: 'competitive-compensation',
  Icon: Wallet,
  title: 'Competitive Compensation',
  desc: 'Compensation goes beyond mere numbers at Locobuzz. We ensure that you receive a salary that reflects your skills and contribution, along with performance bonuses that reward your hard work and achievements.'
}, {
  id: 'health-fitness',
  Icon: ShieldPlus,
  title: 'Health and Wellness',
  desc: 'We get that life is unpredictable and nothing works better than prevention. So we offer our employees comprehensive health insurance for you and your family, ensuring overall peace of mind.'
}, {
  id: 'creative-workspaces',
  Icon: Building2,
  title: 'Creative Workspaces',
  desc: 'Work in modern, creatively designed offices that inspire innovation and collaboration, equipped with the latest technology and amenities.'
}, {
  id: 'team-retreats',
  Icon: PartyPopper,
  title: 'Team Retreats & Social Events',
  desc: 'Enjoy regular team outings, retreats, and social events that strengthen bonds, foster community, and make work fun.'
}, {
  id: 'rewards-recognition',
  Icon: Award,
  title: 'Recognition and Rewards',
  desc: 'Be part of a culture that celebrates your achievements, with recognition programs and rewards for outstanding performance and innovation.'
}];
const GALLERY = ['/careers/event-gallery-2.webp', '/careers/event-gallery-4.webp', '/careers/event-gallery-5.webp'];
export default function CareersClient() {
  const {
    t
  } = useTranslation();
  return <>
      {/* ── HERO ────────────────────────────────────────────────────── */}
      <section className="careers-hero">
        <div className="careers-hero__blob careers-hero__blob--1" aria-hidden="true" />
        <div className="careers-hero__blob careers-hero__blob--2" aria-hidden="true" />

        <div className="careers-hero__inner">
          <h1 className="careers-hero__h1">
            <span className="careers-hero__word careers-hero__word--1">{t("careers.dreamers")}</span>{' '}
            <span className="careers-hero__word careers-hero__word--2">{t("careers.learners")}</span>{' '}
            <span className="careers-hero__word careers-hero__word--3">{t("careers.hustlers")}</span>{' '}
            <span className="careers-hero__word careers-hero__word--4">{t("careers.achievers")}</span>
          </h1>

          <blockquote className="careers-hero__quote">
            <p>{t("careers.ideas_are_great_but_without_executi")}</p>
          </blockquote>

          <p className="careers-hero__sub">
            {t("careers.we_are_a_mixed_bag_of_talent_dynami")}
          </p>

          <div className="careers-hero__ctas">
            <a href="#openings" className="careers-hero__cta">
              {t("careers.see_open_positions")}
              <ArrowDown size={16} strokeWidth={2.4} />
            </a>
          </div>
        </div>
      </section>

      {/* ── AT THE HEART OF LOCOBUZZ — 9 values ─────────────────────── */}
      <section className="careers-values">
        <div className="careers-section__head">
          <span className="careers-kicker">{t("careers.our_culture")}</span>
          <h2 className="careers-h2">{t("careers.at_the_heart_of_locobuzz_lies")}</h2>
          <p className="careers-sub">{t("careers.nine_values_that_shape_how_we_hire")}</p>
        </div>
        <div className="careers-values__grid">
          {VALUES.map(({
          Icon,
          title,
          desc
        }) => <article key={title} className="careers-value">
              <div className="careers-value__icon" aria-hidden="true">
                <Icon size={22} strokeWidth={2.2} />
              </div>
              <h3 className="careers-value__title">{title}</h3>
              <p className="careers-value__desc">{desc}</p>
            </article>)}
        </div>
      </section>

      {/* ── EVENT GALLERY — 3 photos ────────────────────────────────── */}
      <section className="careers-gallery">
        <div className="careers-gallery__grid">
          {GALLERY.map((src, i) => <div key={i} className="careers-gallery__cell">
              <img src={src} alt={t("careers.locobuzz_team_event")} loading="lazy" decoding="async" onError={e => {
            e.currentTarget.style.display = 'none';
          }} />
            </div>)}
        </div>
      </section>

      {/* ── WHY JOIN — 5 benefit cards ──────────────────────────────── */}
      <section className="careers-why">
        <div className="careers-section__head">
          <span className="careers-kicker">{t("careers.why_join_us")}</span>
          <h2 className="careers-h2">{t("careers.why_should_you_join_locobuzz")}</h2>
          <p className="careers-sub">
            {t("careers.by_joining_locobuzz_youre_not_just")}
          </p>
        </div>
        <div className="careers-why__grid">
          {BENEFITS.map(({
          Icon,
          title,
          desc
        }, i) => <article key={title} className={`careers-benefit careers-benefit--${i % 5}`}>
              <div className="careers-benefit__icon" aria-hidden="true">
                <Icon size={28} strokeWidth={2} />
              </div>
              <h3 className="careers-benefit__title">{title}</h3>
              <p className="careers-benefit__desc">{desc}</p>
            </article>)}
        </div>
      </section>

      <InlineStyle css={`
        /* ── Hero ───────────────────────────────────────────────── */
        .careers-hero {
          position: relative;
          overflow: hidden;
          padding: 140px 24px 100px;
          text-align: center;
          background: linear-gradient(180deg, #FFFFFF 0%, #F4F7FF 70%, #F4F7FF 100%);
          font-family: var(--font-inter), system-ui, sans-serif;
        }
        .careers-hero__blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.5;
          pointer-events: none;
        }
        .careers-hero__blob--1 { width: 480px; height: 480px; top: -160px; left: -120px; background: rgba(79, 110, 247, 0.4); }
        .careers-hero__blob--2 { width: 420px; height: 420px; top: 80px; right: -120px; background: rgba(108, 71, 245, 0.35); }
        .careers-hero__inner { position: relative; z-index: 1; max-width: 920px; margin: 0 auto; }

        .careers-hero__h1 {
          font-size: clamp(36px, 6.5vw, 72px);
          font-weight: 900;
          color: #0D1117;
          letter-spacing: -0.035em;
          line-height: 1.05;
          margin: 0 0 32px;
        }
        .careers-hero__word { display: inline-block; }
        .careers-hero__word--1 { color: #4F6EF7; }
        .careers-hero__word--2 { color: #6C47F5; }
        .careers-hero__word--3 { color: #EA4C89; }
        .careers-hero__word--4 { color: #F5A623; }

        .careers-hero__quote {
          max-width: 640px;
          margin: 0 auto 28px;
          padding: 18px 28px;
          background: #FFFFFF;
          border-left: 4px solid var(--brand);
          border-radius: 10px;
          box-shadow: 0 14px 32px -16px rgba(0, 152, 255, 0.18);
          text-align: left;
        }
        .careers-hero__quote p {
          margin: 0;
          font-size: 17px;
          font-style: italic;
          color: var(--text-primary);
          line-height: 1.55;
          font-weight: 600;
        }

        .careers-hero__sub {
          font-size: 17px;
          color: var(--text-body);
          line-height: 1.7;
          max-width: 620px;
          margin: 0 auto 36px;
        }

        .careers-hero__ctas { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
        .careers-hero__cta {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #4F6EF7, #6C47F5);
          color: #FFFFFF;
          font-size: 15px; font-weight: 700;
          text-decoration: none;
          border-radius: 100px;
          box-shadow: 0 12px 28px rgba(79, 110, 247, 0.32);
          transition: transform 0.18s, box-shadow 0.18s;
        }
        .careers-hero__cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 36px rgba(79, 110, 247, 0.45);
        }

        /* ── Section heads ─────────────────────────────────────── */
        .careers-section__head { text-align: center; max-width: 680px; margin: 0 auto 56px; }
        .careers-kicker {
          display: inline-block;
          font-size: 12px;
          font-weight: 800;
          color: var(--brand);
          text-transform: uppercase;
          letter-spacing: 0.14em;
          margin-bottom: 14px;
        }
        .careers-h2 {
          font-size: clamp(28px, 4.2vw, 44px);
          font-weight: 800;
          color: var(--text-primary);
          letter-spacing: -0.025em;
          line-height: 1.1;
          margin: 0 0 12px;
        }
        .careers-sub {
          font-size: 16px;
          color: var(--text-body);
          line-height: 1.7;
          margin: 0;
        }

        /* ── Values grid (9 cards) ────────────────────────────── */
        .careers-values { padding: 100px 24px; background: #FFFFFF; }
        .careers-values__grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .careers-value {
          padding: 28px 26px;
          background: #FFFFFF;
          border: 1.5px solid var(--border-light);
          border-radius: 18px;
          transition: transform 0.25s, border-color 0.25s, box-shadow 0.25s;
        }
        .careers-value:hover {
          transform: translateY(-4px);
          border-color: var(--brand);
          box-shadow: 0 22px 44px -22px rgba(0, 152, 255, 0.22);
        }
        .careers-value__icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: var(--brand-subtle);
          color: var(--brand);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px;
          transition: background 0.25s, color 0.25s;
        }
        .careers-value:hover .careers-value__icon {
          background: linear-gradient(135deg, #4F6EF7, #6C47F5);
          color: #FFFFFF;
        }
        .careers-value__title {
          font-size: 18px;
          font-weight: 800;
          color: var(--text-primary);
          letter-spacing: -0.018em;
          margin: 0 0 10px;
        }
        .careers-value__desc {
          font-size: 14px;
          color: var(--text-body);
          line-height: 1.65;
          margin: 0;
        }

        /* ── Event gallery (3 photos) ─────────────────────────── */
        .careers-gallery { padding: 24px 24px 80px; background: #FFFFFF; }
        .careers-gallery__grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .careers-gallery__cell {
          aspect-ratio: 4 / 3;
          background: linear-gradient(135deg, #EEF2FF 0%, #F5F0FF 100%);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 14px 36px -18px rgba(0, 152, 255, 0.20);
        }
        .careers-gallery__cell img {
          width: 100%; height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.4s;
        }
        .careers-gallery__cell:hover img { transform: scale(1.04); }

        /* ── Why join — 5 benefits ────────────────────────────── */
        .careers-why { padding: 100px 24px; background: #F8FAFF; }
        .careers-why__grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .careers-benefit {
          padding: 32px 28px;
          background: #FFFFFF;
          border: 1.5px solid var(--border-light);
          border-radius: 20px;
          transition: transform 0.25s, box-shadow 0.25s;
          position: relative;
          overflow: hidden;
        }
        .careers-benefit::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, var(--accent-1, #4F6EF7) 0%, var(--accent-2, #6C47F5) 100%);
          opacity: 0;
          transition: opacity 0.3s;
          z-index: 0;
          border-radius: inherit;
        }
        .careers-benefit > * { position: relative; z-index: 1; }
        .careers-benefit:hover {
          transform: translateY(-4px);
          box-shadow: 0 24px 48px -22px rgba(0, 152, 255, 0.30);
        }
        .careers-benefit--0 { --accent-1: #4F6EF7; --accent-2: #6C47F5; }
        .careers-benefit--1 { --accent-1: #10B981; --accent-2: #0891B2; }
        .careers-benefit--2 { --accent-1: #F5A623; --accent-2: #EA4C89; }
        .careers-benefit--3 { --accent-1: #EA4C89; --accent-2: #6C47F5; }
        .careers-benefit--4 { --accent-1: #0891B2; --accent-2: #4F6EF7; }
        .careers-benefit__icon {
          width: 56px; height: 56px;
          border-radius: 16px;
          background: linear-gradient(135deg, var(--accent-1), var(--accent-2));
          color: #FFFFFF;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 20px;
          box-shadow: 0 10px 20px -8px var(--accent-1);
        }
        .careers-benefit__title {
          font-size: 18px;
          font-weight: 800;
          color: var(--text-primary);
          letter-spacing: -0.018em;
          margin: 0 0 12px;
        }
        .careers-benefit__desc {
          font-size: 14px;
          color: var(--text-body);
          line-height: 1.65;
          margin: 0;
        }

        /* ── Responsive ───────────────────────────────────────── */
        @media (max-width: 980px) {
          .careers-values__grid,
          .careers-gallery__grid,
          .careers-why__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 600px) {
          .careers-hero { padding: 100px 16px 72px; }
          .careers-hero__quote { padding: 16px 20px; }
          .careers-hero__quote p { font-size: 15.5px; }
          .careers-values, .careers-why { padding: 64px 16px; }
          .careers-gallery { padding: 16px 16px 56px; }
          .careers-section__head { margin-bottom: 36px; }
          .careers-values__grid,
          .careers-why__grid { grid-template-columns: 1fr; }
          .careers-gallery__grid { grid-template-columns: 1fr; gap: 12px; }
        }
      `} />
    </>;
}